(function () {
  // --------------------------------------------------------------------
  // Personalization toggle show/hide + sanitization
  // --------------------------------------------------------------------
  function toggleNameInput(event) {
    if (!event.target.classList.contains('js-personalization-toggle')) return;
    const container = event.target.closest('.personalization-bundle');
    const wrapper = container?.querySelector('.js-personalization-name-wrapper');
    const nameInput = container?.querySelector('.js-personalization-name');
    if (!container || !wrapper || !nameInput) return;

    wrapper.hidden = !event.target.checked;
    nameInput.disabled = !event.target.checked;

    if (!event.target.checked) {
      nameInput.value = '';
      clearPersonalizationError(nameInput);
    } else {
      nameInput.focus();
    }
  }
  document.addEventListener('change', toggleNameInput);

  function getErrorEl(input) {
    return input.closest('.personalization-bundle')?.querySelector('.js-personalization-error');
  }

  function showPersonalizationError(input, message) {
    const errorEl = getErrorEl(input);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  function clearPersonalizationError(input) {
    const errorEl = getErrorEl(input);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  document.addEventListener('input', (event) => {
    if (!event.target.classList.contains('js-personalization-name')) return;
    const input = event.target;
    const max = parseInt(input.getAttribute('maxlength') || '4', 10);

    if (/[^a-zA-Z]/.test(input.value)) {
      input.value = input.value.replace(/[^a-zA-Z]/g, '');
      showPersonalizationError(input, 'Only letters are allowed.');
    } else {
      clearPersonalizationError(input);
    }

    input.value = input.value.toUpperCase();

    if (input.value.length > max) {
      input.value = input.value.slice(0, max);
    }
  });

  // The bundle variant can have real options (e.g. a color), in which case the
  // main product's "Add on" property should read like "Personalised Tag (Brown)"
  // rather than just the bare product name.
  function getBundleDisplayName(container) {
    const baseName = container?.dataset.bundleProductTitle || '';
    const bundleVariantId = container?.dataset.bundleVariantId;
    const variantSelect = container?.querySelector('.js-personalization-variant-id');
    if (!bundleVariantId || !variantSelect) return baseName;

    const matchingOption = Array.from(variantSelect.options).find((opt) => opt.value === bundleVariantId);
    const variantTitle = matchingOption ? matchingOption.textContent.trim() : '';
    if (!variantTitle || variantTitle === 'Default Title') return baseName;

    return `${baseName} (${variantTitle})`;
  }

  document.addEventListener('change', (event) => {
    if (!event.target.classList.contains('js-personalization-variant-option')) return;

    const container = event.target.closest('.personalization-bundle');
    if (!container) return;

    const optionSelects = Array.from(container.querySelectorAll('.js-personalization-variant-option'));
    const selectedValues = optionSelects.map((select) => select.value);

    const variantSelect = container.querySelector('.js-personalization-variant-id');
    const matchingOption = Array.from(variantSelect?.options || []).find((option) => {
      return [option.dataset.option1, option.dataset.option2, option.dataset.option3]
        .filter(Boolean)
        .every((value, index) => value === selectedValues[index]);
    });

    if (matchingOption && !matchingOption.disabled) {
      container.dataset.bundleVariantId = matchingOption.value;
    }
  });

  // --------------------------------------------------------------------
  // Add-to-cart submission.
  //
  // Gokwik's own SDK intercepts every add-to-cart submit click site-wide on the product page
  // (confirmed live: the theme's native "submit" event on the product form never fires once
  // Gokwik is active, so the previous approach here — inject hidden inputs into the form and
  // wait for a native submit + variant:add — silently never ran at all). Instead of fighting
  // that, this mirrors the same proven pattern already used for the "Personalise Your Bag"
  // global add-on (snippets/addon-global-shared-logic.liquid): intercept the click ourselves in
  // the capture phase (before Gokwik's own listener gets it), build one combined /cart/add.js
  // payload (main product + this bundle's child line, same properties as before), and manually
  // call window.openGokwikSideCart() afterward since Gokwik never saw a submit to react to.
  //
  // If addon-global-shared-logic.liquid is ALSO installed on this product (both "Personalise
  // Your Bag" mechanisms enabled at once), IT already folds this bundle into its own combined
  // payload — so this script must not also intercept the same click, or the two would race and
  // Gokwik could be opened twice / the add submitted twice. window.__zoukGlobalAddonLogicInstalled
  // is the flag that script sets on itself; bail out here if it's present.
  if (window.__zoukGlobalAddonLogicInstalled) return;

  function readPersonalizationItem(isMainProductForm) {
    // This container only ever exists once per page, wired to the main PDP's buy box. The click
    // handler below also has to run for every other add-to-cart button on the page (collection
    // cards, Frequently Bought Together, Recently Viewed, Bestsellers), since Gokwik breaks their
    // native submit too and this is what actually performs the add for them. Without this guard,
    // clicking any of those OTHER products' buttons while this toggle happened to be checked would
    // silently fold the main product's personalization item into that unrelated product's add.
    if (!isMainProductForm) return null;

    const container = document.querySelector('.personalization-bundle');
    if (!container) return null;
    const toggle = container.querySelector('.js-personalization-toggle');
    if (!toggle?.checked) return null;

    const variantId = container.dataset.bundleVariantId;
    if (!variantId) return null;

    const nameInput = container.querySelector('.js-personalization-name');
    const max = parseInt(nameInput?.getAttribute('maxlength') || '4', 10);
    const initials = (nameInput?.value || '').trim().slice(0, max);

    return { variantId, initials, displayName: getBundleDisplayName(container) };
  }

  function readIndependentAddonItems(isMainProductForm) {
    if (!isMainProductForm) return [];
    return Array.from(document.querySelectorAll('.js-addon-toggle:checked'))
      .map((checkbox) => checkbox.dataset.addonVariantId)
      .filter(Boolean);
  }

  function buildItems(mainVariantId, mainQty, mainTitle, isMainProductForm) {
    const personalization = readPersonalizationItem(isMainProductForm);
    const independentVariantIds = readIndependentAddonItems(isMainProductForm);
    const items = [];

    const mainProperties = {};
    if (personalization) {
      const bundleTimestamp = Date.now().toString();
      mainProperties['_bundleTimestamp'] = bundleTimestamp;
      mainProperties['_bundleRole'] = 'main';
      mainProperties['Add on'] = personalization.initials
        ? `${personalization.displayName}, Initial-${personalization.initials}`
        : personalization.displayName;

      items.push({ id: parseInt(mainVariantId, 10), quantity: mainQty, properties: mainProperties });

      const childProperties = { '_bundleTimestamp': bundleTimestamp, '_bundleRole': 'child', 'Ordered for': mainTitle };
      if (personalization.initials) childProperties['Initial'] = personalization.initials;
      items.push({ id: parseInt(personalization.variantId, 10), quantity: mainQty, properties: childProperties });
    } else {
      items.push({ id: parseInt(mainVariantId, 10), quantity: mainQty });
    }

    independentVariantIds.forEach((variantId) => {
      items.push({ id: parseInt(variantId, 10), quantity: 1 });
    });

    return items;
  }

  async function submitBundledCart(form, isMainProductForm) {
    const mainVariantId = form.querySelector('[name="id"]')?.value;
    if (!mainVariantId) return false;
    // See the identical comment in addon-global-shared-logic.liquid: the quantity input isn't
    // necessarily a DOM descendant of `form` (the sticky add-to-cart bar renders it separately,
    // linked only via form="..."), so form.elements (which includes externally form-associated
    // fields) is used rather than form.querySelector().
    const mainQty = parseInt(form.elements.namedItem('quantity')?.value || '1', 10);
    const mainTitle = document.title.split('|')[0].trim();
    const items = buildItems(mainVariantId, mainQty, mainTitle, isMainProductForm);

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
      if (response.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        window.openGokwikSideCart?.();

        // Only reset the main product's own state when this add actually belonged to the main
        // product form — an unrelated add from a collection card / Frequently Bought Together /
        // Recently Viewed / Bestsellers button must never clear selections the shopper made for
        // the main product that's still sitting unsubmitted.
        if (isMainProductForm) {
          const container = document.querySelector('.personalization-bundle');
          if (container) {
            const toggle = container.querySelector('.js-personalization-toggle');
            const wrapper = container.querySelector('.js-personalization-name-wrapper');
            const nameInput = container.querySelector('.js-personalization-name');
            if (toggle) toggle.checked = false;
            if (wrapper) wrapper.hidden = true;
            if (nameInput) {
              nameInput.value = '';
              nameInput.disabled = true;
            }
          }
          document.querySelectorAll('.js-addon-toggle:checked').forEach((checkbox) => { checkbox.checked = false; });
        }
      } else {
        console.error('[personalization-bundle] add failed', await response.json());
      }
    } catch (err) {
      console.error('[personalization-bundle] add threw', err);
    }
    return true;
  }

  document.addEventListener('click', (event) => {
    const submitControl = event.target.closest('[type="submit"]');
    if (!submitControl) return;

    const form = submitControl.closest('form');
    if (!form || !form.action.includes('/cart/add')) return;

    // Same [data-atc-source] convention used by product-card.liquid / assets/theme.js. The main
    // PDP's own buy box is tagged data-atc-source="pdp_main" in buy-buttons.liquid; every other
    // add-to-cart button on the page (collection cards, Frequently Bought Together, Recently
    // Viewed, Bestsellers) carries a different source. This same click handler has to run for all
    // of them too (it's what performs their actual add, since Gokwik breaks native submit
    // everywhere), so the personalization checks below must only apply to the main product's own
    // button — otherwise an unrelated product's add could get wrongly blocked or bundled by
    // whatever the main product's toggle happens to be set to.
    const source = submitControl.closest('[data-atc-source]')?.getAttribute('data-atc-source') || null;
    const isMainProductForm = source === 'pdp_main';

    const personalizationToggle = isMainProductForm && document.querySelector('.personalization-bundle .js-personalization-toggle:checked');
    if (personalizationToggle) {
      const nameInput = document.querySelector('.personalization-bundle .js-personalization-name');
      if (nameInput && !nameInput.value.trim()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showPersonalizationError(nameInput, 'Please enter initials to personalize this item.');
        nameInput.focus();
        return;
      }
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    submitBundledCart(form, isMainProductForm);
  }, true); // capture: true — must run before Gokwik's own click interception

  // --------------------------------------------------------------------
  // Reconciliation: only cascades when the MAIN item is removed.
  // --------------------------------------------------------------------
  let isReconciling = false;

  async function reconcileBundles() {
    if (isReconciling) return;
    isReconciling = true;

    try {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

      const mainTimestampsPresent = new Set();
      const childrenByTimestamp = {};

      cart.items.forEach((item) => {
        const timestamp = item.properties && item.properties._bundleTimestamp;
        const role = item.properties && item.properties._bundleRole;
        if (!timestamp || !role) return;

        if (role === 'main') {
          mainTimestampsPresent.add(timestamp);
        } else if (role === 'child') {
          childrenByTimestamp[timestamp] = childrenByTimestamp[timestamp] || [];
          childrenByTimestamp[timestamp].push(item.key);
        }
      });

      const updates = {};
      Object.entries(childrenByTimestamp).forEach(([timestamp, keys]) => {
        if (!mainTimestampsPresent.has(timestamp)) {
          keys.forEach((key) => { updates[key] = 0; });
        }
      });

      if (Object.keys(updates).length === 0) return;

      await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (err) {
      console.error('Bundle linking: reconciliation failed', err);
    } finally {
      isReconciling = false;
    }
  }

  document.addEventListener('cart:change', () => {
    setTimeout(reconcileBundles, 300);
  });
})();
